import { z } from 'zod';

import { ViewerIdSchema, viewerKey, type ViewerId } from './viewer.js';

/**
 * Reveal payloads — recorded as ops on the same op_log substrate as fact
 * mutations (02-architecture.md §4.1, 03-information-model.md §10).
 *
 * Granularity: a reveal targets a SINGLE proposition, identified by
 * (entity subject, predicate). Revealing one fact on a subject does not
 * leak siblings — this is the per-attribute reveal invariant the
 * differentiator analysis (10-comparisons.md §5) hinges on.
 *
 * `unreveal` exists in the shape but is BLOCKED at the API level — reveals
 * are write-once at every phase per 00-vision.md.
 */

/**
 * `RevealSource` (03-information-model.md §10) — provenance tag attached to
 * every reveal op. The `kind` field is INTENTIONALLY an open string: the
 * substrate validates only that it is non-empty/non-whitespace. New flows
 * (handouts, automations, packs, future ratification UX) add their own
 * kinds without a schema migration.
 *
 * Known conventions (do not switch on these without a default branch):
 *
 * - `'gm_manual'` — GM clicked "reveal to V" on a fact. No `ref`.
 * - `'session_reveal'` — Reveal authored inside an active session (HUD /
 *   Reveal Composer). `ref` may carry a Scene or Session id.
 * - `'auto_visibility'` — Automatic rule (e.g. "all PCs see canon at session
 *   start"). `ref` is the rule id.
 * - `'pack_seed'` — Reveal carried in from a pack at load time. `ref` is
 *   the pack manifest id.
 * - `'import'` — Migration / cross-Campaign import (Pack subscription or
 *   Viewer projection import). `ref` is a free-form source identifier.
 * - `'rollback'` — Reveal re-emitted as part of a Campaign snapshot
 *   restore (Phase 3). `ref` is the snapshot id.
 *
 * The 03-information-model.md §10 table documents additional product-level
 * kinds (`facet`, `scene`, `handout`, `auto_ratify`, `extraction`,
 * `auditor_suggestion`, `belief_share`, `automation`) that flows may emit
 * as they come online. Renderers MUST treat unknown kinds as a generic
 * "Other" bucket — never throw, never key on a closed enum.
 */
export const RevealSourceSchema = z
  .object({
    kind: z
      .string()
      .min(1, 'RevealSource.kind must be non-empty')
      .refine((s) => s.trim().length > 0, 'RevealSource.kind must not be whitespace-only'),
    ref: z.string().min(1).optional(),
  })
  .strict();
export type RevealSource = z.infer<typeof RevealSourceSchema>;

/**
 * Suggested kinds for autocompletion / UI grouping. NOT an enum — the
 * substrate accepts any non-empty string. Callers MUST NOT switch on
 * `RevealSource.kind` without a default branch.
 */
export const SUGGESTED_REVEAL_SOURCE_KINDS = [
  'gm_manual',
  'session_reveal',
  'auto_visibility',
  'pack_seed',
  'import',
  'rollback',
] as const;
export type SuggestedRevealSourceKind = (typeof SUGGESTED_REVEAL_SOURCE_KINDS)[number];

/**
 * Default source for reveals authored without an explicit provenance —
 * applied by the back-compat shim when an old (pre-#37) reveal op is
 * parsed from the op log. New code paths should always set `source`
 * explicitly.
 */
export const DEFAULT_REVEAL_SOURCE: RevealSource = { kind: 'gm_manual' };

/**
 * Back-compat shim: reveal ops authored before #37 landed do not carry a
 * `source` field. The Walking Skeleton `'demo'` Campaign has such ops.
 * We default missing sources to `{ kind: 'gm_manual' }` at parse time so
 * the op log replays without rewriting history.
 *
 * Remove only when the entire production op corpus has been migrated to
 * carry an explicit `source`.
 */
export const RevealPayloadSchema = z
  .object({
    kind: z.literal('reveal'),
    viewer: ViewerIdSchema,
    predicate: z.string().min(1),
    source: RevealSourceSchema.default(DEFAULT_REVEAL_SOURCE),
  })
  .strict();
export type RevealPayload = z.infer<typeof RevealPayloadSchema>;

export const UnrevealPayloadSchema = z
  .object({
    kind: z.literal('unreveal'),
    viewer: ViewerIdSchema,
    predicate: z.string().min(1),
    source: RevealSourceSchema.default(DEFAULT_REVEAL_SOURCE),
  })
  .strict();
export type UnrevealPayload = z.infer<typeof UnrevealPayloadSchema>;

export type RevealOpPayload = RevealPayload | UnrevealPayload;

export const RevealOpPayloadSchema = z.discriminatedUnion('kind', [
  RevealPayloadSchema,
  UnrevealPayloadSchema,
]);

/**
 * Builder for a reveal payload. `source` defaults to `gm_manual` for the
 * common "GM clicked reveal" path; flows with other provenance (session
 * HUD, auto-visibility rules, pack seeds, …) pass an explicit source.
 */
export function revealFactPayload(
  viewer: ViewerId,
  predicate: string,
  source: RevealSource = DEFAULT_REVEAL_SOURCE,
): RevealPayload {
  return { kind: 'reveal', viewer, predicate, source };
}

/**
 * Reserved op-log envelope prefix for reveal ops. A reveal op's wire
 * envelope (`subject`/`predicate`) is distinct from its payload: the
 * `subject` is the entity the targeted fact lives on, and the `predicate`
 * is a SYNTHETIC slot minted here — `reveal:<targetPredicate>:<viewerKey>`.
 *
 * Why a synthetic slot: the substrate chains ops by `(subject, predicate)`
 * and tracks one head per chain. Each reveal is write-once and independent
 * (`base_version: null`), so every `(viewer, fact)` reveal needs its OWN
 * chain key to avoid colliding with the fact's value chain or with another
 * viewer's reveal of the same fact. The projection function keys reveals
 * off `payload.kind === 'reveal'` (not the envelope), so this slot is purely
 * a chain-identity device — never parsed for meaning by `project()`.
 */
export const REVEAL_ENVELOPE_PREFIX = 'reveal:';

/**
 * Mint the op-log envelope predicate for revealing `targetPredicate` on an
 * entity to `viewer`. Deterministic: the same `(predicate, viewer)` always
 * yields the same slot, so a re-reveal folds onto the same single-op chain
 * rather than spawning a duplicate (the UI guards against re-revealing an
 * already-revealed viewer regardless).
 */
export function revealEnvelopePredicate(targetPredicate: string, viewer: ViewerId): string {
  return `${REVEAL_ENVELOPE_PREFIX}${targetPredicate}:${viewerKey(viewer)}`;
}

/** True when a predicate slot was minted by {@link revealEnvelopePredicate}. */
export function isRevealEnvelopePredicate(predicate: string): boolean {
  return predicate.startsWith(REVEAL_ENVELOPE_PREFIX);
}

/**
 * Which validity bound of a segment a reveal facet targets (ADR-0005 §7).
 * A segment's bounds are *independently revealable facets*: a Viewer revealed an
 * open "Aria is ruler" (the segment value) does NOT learn she fell when
 * `fact.succeed` stamps her `to` — the `to`-bound is a separate facet, hidden
 * until separately revealed.
 *
 * S4 scope (#191): the projection consults ONLY the **`to`** facet (the *close*
 * — the stale-knowledge vector). The `from` rides with the segment's value
 * (revealing "X became ruler" inherently includes "became"); clamping `from`
 * open-backward would fabricate history and leak the value into the past. The
 * `'from'` member is retained for forward-compat / authoring symmetry — a
 * `from`-facet reveal is collected but not (yet) projection-consulted.
 */
export type SegmentBound = 'from' | 'to';

/**
 * Reserved suffix marking a reveal whose TARGET is a segment's validity *bound*
 * rather than the segment's value (ADR-0005 §7). The reveal payload's
 * `predicate` becomes `<segmentKey>::bound:from` / `<segmentKey>::bound:to`.
 *
 * Why a synthetic target predicate (not a new op kind): a bound-facet reveal is
 * an ordinary write-once reveal op — same shape, same projection key path
 * (`revealsForViewer` collects it into the viewer's visible-key set). Keying it
 * off a distinct target predicate lets the projection ask "is this segment's
 * `to` revealed to V?" without a schema migration, and keeps the bound facet on
 * the SAME granularity rail as per-attribute facet reveal. The `::` separator
 * (excluded from minted ids / the UUID alphabet) keeps `<segmentKey>::bound:to`
 * unambiguous against the segment value's own reveal target (`<segmentKey>`).
 */
export const SEGMENT_BOUND_FACET_SEP = '::bound:';

/**
 * Mint the reveal-target predicate for a segment's validity bound facet. Pair
 * with {@link revealFactPayload} to author the reveal op; the projection clamps
 * a non-GM viewer's view of the segment validity to the bounds they hold (a
 * bound not revealed reads as open in that direction → stale value retained).
 *
 * `segmentChainKey` is the segment's op-log key (`predicate` or
 * `predicate#seg_…`) — the same value used as the segment value's reveal target.
 */
export function boundFacetPredicate(segmentChainKey: string, bound: SegmentBound): string {
  return `${segmentChainKey}${SEGMENT_BOUND_FACET_SEP}${bound}`;
}

/** True when a reveal target was minted by {@link boundFacetPredicate}. */
export function isBoundFacetPredicate(predicate: string): boolean {
  return predicate.includes(SEGMENT_BOUND_FACET_SEP);
}

/**
 * Split a bound-facet reveal target back into `{ segmentChainKey, bound }`, or
 * `null` when `predicate` is not a bound-facet target. The split is on the LAST
 * occurrence of the separator so a segment key that itself contained the token
 * (it cannot — `::` is reserved) would still round-trip the bound suffix.
 */
export function splitBoundFacetPredicate(
  predicate: string,
): { segmentChainKey: string; bound: SegmentBound } | null {
  const idx = predicate.lastIndexOf(SEGMENT_BOUND_FACET_SEP);
  if (idx < 0) return null;
  const segmentChainKey = predicate.slice(0, idx);
  const rawBound = predicate.slice(idx + SEGMENT_BOUND_FACET_SEP.length);
  if (rawBound !== 'from' && rawBound !== 'to') return null;
  return { segmentChainKey, bound: rawBound };
}

/**
 * The set of `factKey(subject, <segmentKey>::bound:<bound>)` strings revealed to
 * `viewer`. The projection consults this to decide which validity bounds of a
 * segment a non-GM viewer may observe; an unrevealed bound is clamped to "open"
 * in that direction so the viewer retains the stale value (ADR-0005 §7).
 *
 * Returned as a `Set` of factKeys (subject-scoped) so a bound reveal on one
 * entity cannot leak to a same-segment-id chain on another entity.
 */
export function revealedBoundsForViewer(ops: Iterable<RevealOpRef>, viewer: ViewerId): Set<string> {
  const key = viewerKey(viewer);
  const out = new Set<string>();
  for (const { subject, payload } of ops) {
    if (payload.kind !== 'reveal') continue;
    if (viewerKey(payload.viewer) !== key) continue;
    if (!isBoundFacetPredicate(payload.predicate)) continue;
    out.add(factKey(subject, payload.predicate));
  }
  return out;
}

export class UnrevealNotSupportedError extends Error {
  constructor() {
    super('Reveals are write-once at every phase (see 00-vision.md). unreveal is not exposed.');
    this.name = 'UnrevealNotSupportedError';
  }
}

/** Composite key for a (subject, predicate) fact identity. */
export function factKey(subject: string, predicate: string): string {
  return `${subject}/${predicate}`;
}

interface RevealOpRef {
  /** The entity subject the reveal targets. */
  subject: string;
  payload: RevealOpPayload;
}

/**
 * Set of `factKey(subject, predicate)` strings visible to the viewer.
 */
export function revealsForViewer(ops: Iterable<RevealOpRef>, viewer: ViewerId): Set<string> {
  const key = viewerKey(viewer);
  const out = new Set<string>();
  for (const { subject, payload } of ops) {
    if (payload.kind !== 'reveal') continue;
    if (viewerKey(payload.viewer) !== key) continue;
    // Bound-facet reveals (`<segmentKey>::bound:from|to`) gate validity bounds,
    // not the segment VALUE — they are consumed by `revealedBoundsForViewer`.
    // Keep them out of the value visible-key set (ADR-0005 §7).
    if (isBoundFacetPredicate(payload.predicate)) continue;
    out.add(factKey(subject, payload.predicate));
  }
  return out;
}

/** Set of viewer keys a specific (subject, predicate) fact has been revealed to. */
export function revealersOfFact(
  ops: Iterable<RevealOpRef>,
  subject: string,
  predicate: string,
): Set<string> {
  const out = new Set<string>();
  for (const { subject: opSubject, payload } of ops) {
    if (payload.kind !== 'reveal') continue;
    if (opSubject !== subject || payload.predicate !== predicate) continue;
    out.add(viewerKey(payload.viewer));
  }
  return out;
}
